import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { LanguageProvider } from '../context/LanguageContext.jsx';
import { ThemeProvider } from '../context/ThemeContext.jsx';
import { TranslationProvider } from '../context/TranslationContext.jsx';
import { useTranslatedText } from './i18n/useTranslatedText.js';
import AnalyticsTracker from './analytics/AnalyticsTracker.jsx';
import EventCard from './event/eventcard/EventCard.jsx';
import EventConfirmation from './event/eventconfirmation/EventConfirmation.jsx';
import PendingEventDetail from './event/eventconfirmation/PendingEventDetail.jsx';
import CreateEvent from './event/eventcreation/CreateEvent.jsx';
import EventDetail from './event/eventdetail/EventDetail.jsx';
import MyEvents from './event/myevents/MyEvents.jsx';
import RegisteredUsers from './event/myevents/RegisteredUsers.jsx';
import RejectedEventNotification from './event/rejectedevent/RejectedEvent.jsx';
import { RejectedEventsProvider, useRejectedEvents } from './event/rejectedevent/RejectedEventsContext.jsx';
import FilterSection from './filtersection/FilterSection.jsx';
import Footer from './footer/Footer.jsx';
import Header from './header/Header.jsx';
import EmailVerification from './auth/EmailVerification.jsx';
import GoogleSignupComplete from './auth/GoogleSignUpComplete.jsx';
import Login from './auth/Login';
import Signup from './auth/Signup';
import ResetPassword from './auth/resetpassword/ResetPassword.jsx';
import MapComponent from './map/MapComponent.jsx';
import MapScriptLoader from './map/MapScriptLoader.jsx';
import Profile from './profile/Profile.jsx';
import CreateContent from './resources/CreateContent.jsx';
import MyResources from './resources/MyResources.jsx';
import PendingResourceDetail from './resources/PendingResourceDetail.jsx';
import ResourceCard from './resources/ResourceCard.jsx';
import ResourceConfirm from './resources/ResourceConfirm.jsx';
import ResourceCreation from './resources/ResourceCreation.jsx';
import ResourceDetail from './resources/ResourceDetail.jsx';
import Search from './search/Search.jsx';
import './App.css';

const UserChecker = ({ children }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const loadingText = useTranslatedText('Yükleniyor...');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setChecking(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (!userData.username || !userData.age) {
            navigate('/google-kayıt-tamamla');
          }
        }
      } catch (error) {
        console.error('Error checking user data:', error);
      } finally {
        setChecking(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  if (checking) return <div>{loadingText}</div>;
  return children;
};

const RejectedEventCheck = () => {
  const { rejectedEvents } = useRejectedEvents();
  const navigate = useNavigate();
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (!hasChecked && rejectedEvents.length > 0) {
      navigate(`/etkinlik-reddedildi/${rejectedEvents[0].id}`);
      setHasChecked(true);
    }
  }, [hasChecked, navigate, rejectedEvents]);

  return null;
};

const AppContent = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentResourcePage, setCurrentResourcePage] = useState(1);

  const eventsPerPage = 6;
  const resourcesPerPage = 6;

  const previousText = useTranslatedText('Önceki');
  const nextText = useTranslatedText('Sonraki');
  const pageText = useTranslatedText('Sayfa');
  const noEventsText = useTranslatedText('Etkinlik Bulunamadı');
  const resourcesText = useTranslatedText('Kaynaklar');
  const noResourcesText = useTranslatedText('Kaynak Bulunamadı');
  const loadingText = useTranslatedText('Yükleniyor...');

  useEffect(() => {
    const fetchEvents = async () => {
      const q = query(collection(db, 'events'), where('status', '==', 'approved'));
      const eventsSnapshot = await getDocs(q);
      const list = eventsSnapshot.docs.map((snap) => ({
        id: snap.id,
        ...snap.data(),
      }));
      setEvents(list);
      setFilteredEvents(list);
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchResources = async () => {
      const q = query(collection(db, 'resources'), where('status', '==', 'confirmed'));
      const resourcesSnapshot = await getDocs(q);
      const list = resourcesSnapshot.docs.map((snap) => ({
        id: snap.id,
        ...snap.data(),
      }));
      setFilteredResources(list);
    };
    fetchResources();
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      try {
        if (!user) {
          setIsAdmin(false);
          return;
        }
        const docSnap = await getDoc(doc(db, 'users', user.uid));
        setIsAdmin(Boolean(docSnap.exists() && docSnap.data().isAdmin));
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const sortedEvents = useMemo(
    () => [...filteredEvents].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [filteredEvents]
  );
  const totalPages = Math.ceil(sortedEvents.length / eventsPerPage);
  const currentEvents = useMemo(() => {
    const start = (currentPage - 1) * eventsPerPage;
    return sortedEvents.slice(start, start + eventsPerPage);
  }, [currentPage, sortedEvents]);

  const sortedResources = useMemo(
    () => [...filteredResources].sort((a, b) => a.title.localeCompare(b.title)),
    [filteredResources]
  );
  const resourceTotalPages = Math.ceil(sortedResources.length / resourcesPerPage);
  const currentResources = useMemo(() => {
    const start = (currentResourcePage - 1) * resourcesPerPage;
    return sortedResources.slice(start, start + resourcesPerPage);
  }, [currentResourcePage, sortedResources]);

  const handleSearch = (queryText) => {
    const filtered = events.filter((event) => event.name.toLowerCase().includes(queryText.toLowerCase()));
    setFilteredEvents(filtered);
    setCurrentPage(1);
  };

  const handleFilter = (filters) => {
    const minCapacityFilter = filters.minCapacity ? parseInt(filters.minCapacity, 10) : null;
    const filtered = events.filter((event) => {
      const eventCapacity = typeof event.maxCapacity === 'number' ? event.maxCapacity : parseInt(event.maxCapacity, 10);
      return (
        (!filters.location || (event.location && event.location.toLowerCase().includes(filters.location.toLowerCase()))) &&
        (!filters.date || event.date === filters.date) &&
        (!filters.organizer || (event.organizer && event.organizer.toLowerCase().includes(filters.organizer.toLowerCase()))) &&
        (!filters.ageRangeFilter || event.ageRange === filters.ageRangeFilter) &&
        (minCapacityFilter == null || (Number.isFinite(eventCapacity) && eventCapacity >= minCapacityFilter))
      );
    });
    setFilteredEvents(filtered);
    setCurrentPage(1);
  };

  return (
    <>
      <AnalyticsTracker />
      <UserChecker>
        <div className="App">
          <Header />
          <MapScriptLoader>
            <RejectedEventCheck />
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <Search onSearch={handleSearch} />
                    <FilterSection onFilter={handleFilter} />
                    <MapComponent events={events} filteredEvents={filteredEvents} />

                    {sortedEvents.length > 0 ? (
                      <>
                        <div className="event-grid">
                          {currentEvents.map((event) => (
                            <EventCard key={event.id} event={event} />
                          ))}
                        </div>
                        <div className="pagination">
                          <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                            {previousText}
                          </button>
                          <span>{currentPage}. {pageText}</span>
                          <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>
                            {nextText}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="no-events-message">
                        <p>{noEventsText}</p>
                      </div>
                    )}

                    <h2 className="section-heading">{resourcesText}</h2>
                    {sortedResources.length > 0 ? (
                      <>
                        <div className="resource-grid">
                          {currentResources.map((resource) => (
                            <ResourceCard key={resource.id} resource={resource} />
                          ))}
                        </div>
                        <div className="pagination">
                          <button
                            onClick={() => setCurrentResourcePage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentResourcePage === 1}
                          >
                            {previousText}
                          </button>
                          <span>{currentResourcePage}. {pageText}</span>
                          <button
                            onClick={() => setCurrentResourcePage((prev) => Math.min(prev + 1, resourceTotalPages))}
                            disabled={currentResourcePage === resourceTotalPages}
                          >
                            {nextText}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="no-resources-message">
                        <p>{noResourcesText}</p>
                      </div>
                    )}
                  </>
                }
              />

              <Route
                path="/içerik-onayla"
                element={
                  loading ? (
                    <div>{loadingText}</div>
                  ) : isAdmin ? (
                    <>
                      <EventConfirmation />
                      <ResourceConfirm />
                    </>
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              <Route path="/giriş-yap" element={<Login />} />
              <Route path="/hesap-oluştur" element={<Signup />} />
              <Route path="/profil" element={<Profile />} />
              <Route
                path="/benim-etkinliklerim"
                element={
                  <>
                    <MyEvents />
                    <MyResources />
                  </>
                }
              />
              <Route path="/etkinlik-oluştur" element={<CreateEvent />} />
              {isAdmin && !loading && <Route path="/etkinlik-onayla/:eventId" element={<PendingEventDetail />} />}
              <Route path="/etkinlik/:eventId" element={<EventDetail />} />
              <Route path="/email-doğrulama" element={<EmailVerification />} />
              <Route path="/etkinlik-reddedildi/:eventId" element={<RejectedEventNotification />} />
              <Route path="/kayıtlı-kişiler/:eventId" element={<RegisteredUsers />} />
              <Route path="/şifre-değiştirme" element={<ResetPassword />} />
              <Route path="/google-kayıt-tamamla" element={<GoogleSignupComplete />} />
              <Route path="/resource/:resourceId" element={<ResourceDetail />} />
              <Route path="/resource-oluştur" element={<ResourceCreation />} />
              {isAdmin && !loading && <Route path="/resource-onayla/:resourceId" element={<PendingResourceDetail />} />}
              <Route path="/içerik-seç" element={<CreateContent />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Footer />
          </MapScriptLoader>
        </div>
      </UserChecker>
    </>
  );
};

const App = () => (
  <RejectedEventsProvider>
    <ThemeProvider>
      <LanguageProvider>
        <TranslationProvider>
          <Router>
            <AppContent />
          </Router>
        </TranslationProvider>
      </LanguageProvider>
    </ThemeProvider>
  </RejectedEventsProvider>
);

export default App;
